/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useStoreContext } from '@/contexts/StoreContext';
import { useLocalStorage } from 'primereact/hooks';
import { Messages } from 'primereact/messages';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const FrenchMessage = ({ href }: { href: string }) => {
  return (
    <>
      <span className="font-medium">important ! &nbsp;</span>
      Vous accédez aux projets public de AntFlow. Veuillez ajouter votre gitlab token dans votre&nbsp;
      <a href={href} target="__blank" className="text-color font-bold underline">
        profil utilisateur
      </a>
      &nbsp;et <span className="font-semibold">déconnectez-vous</span> puis{' '}
      <span className="font-semibold">reconnectez-vous</span> pour que la modification soit prise en compte.
    </>
  );
};

const EnglishMessage = ({ href }: { href: string }) => {
  return (
    <>
      <span className="font-medium">Warning ! &nbsp;</span>
      You are only accessing public AntFlow's projects. Please add your GitLab token in your&nbsp;
      <a href={href} target="__blank" className="text-color font-bold underline">
        user profile
      </a>
      &nbsp;and <span className="font-semibold">log out</span> then <span className="font-semibold">log in</span> for
      the change to take effect.
    </>
  );
};
const LocalzationMessage = {
  fr: FrenchMessage,
  en: EnglishMessage
};

export const AlertMessage: React.FC = () => {
  const { currentUser, antflowServerConfig } = useStoreContext();
  const { i18n } = useTranslation();
  const msgRef = useRef<Messages>(null);
  const [dismissed, setDismissed] = useLocalStorage<boolean>(false, 'hide_token_alert');

  const showTokenAlert =
    antflowServerConfig.oidc?.provider === 'keycloak' && currentUser?.git_token === null && !dismissed;

  const MessageComponent =
    LocalzationMessage[i18n.language as keyof typeof LocalzationMessage] || LocalzationMessage['fr'];

  const onDismiss = () => {
    setDismissed(true);
  };

  useEffect(() => {
    if (showTokenAlert && msgRef.current) {
      msgRef.current.clear();
      msgRef.current.show({
        severity: 'warn',
        sticky: true,
        closable: true,
        content: (
          <div className="text-sm">
            <MessageComponent href={currentUser.profile} />
          </div>
        )
      });
    }
    return () => {
      msgRef.current?.clear();
    };
  }, [msgRef, showTokenAlert, currentUser, MessageComponent]);

  if (!showTokenAlert || !currentUser || dismissed) {
    return null;
  }

  return <Messages pt={{ root: { className: 'm-0 border-noround' } }} onRemove={onDismiss} ref={msgRef} />;
};
